import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { User, Mail, Crown, LogOut, ChevronRight, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const st = user?.subscription_state;
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success("Email di verifica inviata. Controlla la tua casella."); }
    catch { toast.error("Impossibile inviare l'email. Riprova."); }
    finally { setBusy(false); }
  };
  const planLabel = st?.plan === "trial"
    ? (st?.expired ? "Prova terminata" : `Prova · ${st?.days_remaining ?? 0} gg rimanenti`)
    : (user?.subscription_plan || "trial");
  return (
    <div className="px-4 pt-6">
      <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Profilo</h1>
      <div className="mt-6 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-blue-500 text-white grid place-items-center font-display font-bold text-2xl">{(user?.first_name || user?.name || "?")[0]?.toUpperCase()}</div>
        <div><p className="font-display font-bold text-lg text-slate-900">{user?.first_name || user?.name}</p><p className="text-sm text-slate-500">{user?.email}</p></div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 bg-white divide-y divide-slate-100">
        <Row icon={User} label="Nome" value={user?.first_name || user?.name} />
        <Row icon={Mail} label="Email" value={user?.email} />
        <Row icon={st?.plan === "trial" ? Clock : Crown} label="Piano" value={<span className="capitalize text-blue-600 font-semibold">{planLabel}</span>} />
      </div>

      {user && !user.email_verified && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="verify-email-card">
          <p className="font-semibold text-amber-900 text-sm">Conferma il tuo indirizzo email</p>
          <p className="text-xs text-amber-700 mt-0.5">Ti abbiamo inviato un link a {user.email}. Confermalo per proteggere il tuo account e migliorare la consegna delle email.</p>
          <button onClick={resend} disabled={busy} data-testid="resend-verification" className="mt-2 text-sm font-semibold text-amber-800 underline disabled:opacity-60">{busy ? "Invio…" : "Reinvia email di verifica"}</button>
        </div>
      )}

      <button onClick={() => navigate("/dashboard/billing")} data-testid="settings-manage-billing"
        className="mt-4 w-full flex items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3.5 text-blue-700 font-semibold">
        <span className="flex items-center gap-2"><Crown className="w-4 h-4" /> Gestisci abbonamento</span>
        <ChevronRight className="w-4 h-4" />
      </button>

      <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="mt-4 w-full flex items-center justify-center gap-2 rounded-2xl border border-slate-200 text-slate-700 font-semibold py-3.5"><LogOut className="w-4 h-4" /> Esci</button>
      <div className="mt-4 rounded-2xl bg-slate-100 p-4 text-sm text-slate-500 text-center">Login con Apple, membri del team ed export PDF in arrivo.</div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-xl bg-slate-100 grid place-items-center text-slate-500"><Icon className="w-4 h-4" /></div><div><p className="text-sm text-slate-400">{label}</p><p className="font-semibold text-slate-900">{value}</p></div></div>
);
